import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from '@helpcenter/analytics-components';
import { HtmlAnchor } from '@helpcenter/analytics-components/src/html-anchor';
import { fetchUserModelAction } from '@helpcenter/epics-models/src/user';
import { getHelpCenterAri } from '@helpcenter/meta';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useRouter } from 'react-resource-router';

import Badge from '@atlaskit/badge';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { CustomItem, Section } from '@atlaskit/menu';
import { getEnv } from '@atlassian/help-center-common-util/env';

import { fetchOrganisationsModelAction } from '../../epics/model/organisations';
import { fetchUserRequestsCountsAction } from '../../epics/model/user-requests-counts';
import type { State } from '../../state';
import { getCanAdministerJIRA } from '../../state/selectors/user';
import { getUserRequestsCounts } from '../../state/selectors/user-requests-counts';
import { parseHelpCenterAri } from '../../utils/ari-parser';

import messages from './messages';

interface StateProps {
	myRequestCount?: number;
	pendingApprovalCount?: number;
	approvalCount?: number;
	canAdministerJIRA: boolean;
}

interface DispatchProps {
	fetchUser: typeof fetchUserModelAction;
	fetchUserRequestsCounts: typeof fetchUserRequestsCountsAction;
	fetchOrganisations: typeof fetchOrganisationsModelAction;
}

type Props = StateProps & DispatchProps;

const HubAvatarMenuOptionsComponent = ({
	fetchUser,
	fetchUserRequestsCounts,
	fetchOrganisations,
	canAdministerJIRA,
	myRequestCount = 0,
	pendingApprovalCount = 0,
	approvalCount = 0,
}: Props) => {
	di(getEnv);
	const { formatMessage } = useIntl();
	const [{ match }] = useRouter();

	// Fetch user data and request counts on component mount
	useEffect(() => {
		fetchUser();
		fetchOrganisations();
		fetchUserRequestsCounts();
	}, [fetchUser, fetchOrganisations, fetchUserRequestsCounts]);

	// Calculate total pending items for badges
	const totalRequestsCount = myRequestCount || 0;
	const totalApprovalsCount = pendingApprovalCount || 0;
	const { portalId, id: pageId } = match.params;

	const showApprovals = approvalCount > 0 || pendingApprovalCount > 0;
	const helpCenterAri = getHelpCenterAri();
	const helpCenterId = parseHelpCenterAri(helpCenterAri).helpCenterId;

	let editHubSuffix = '';
	if (portalId) {
		editHubSuffix = `/portal/${portalId}`;
	} else if (pageId) {
		editHubSuffix = `/${pageId}`;
	}

	return (
		<>
			{!FeatureGates.checkGate('hub_component_upgrade') && (
				<Section hasSeparator>
					<CustomItem
						component={Link}
						actionSubjectId="navbarUserRequests"
						to="/user/requests?reporter=all&statuses=open"
						iconAfter={totalRequestsCount > 0 ? <Badge>{totalRequestsCount}</Badge> : undefined}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						className="cv-exclude-from-customisation"
					>
						{formatMessage(messages.requests)}
					</CustomItem>
					{showApprovals && (
						<CustomItem
							component={Link}
							actionSubjectId="navbarUserApprovals"
							to="/user/approvals"
							iconAfter={totalApprovalsCount > 0 ? <Badge>{totalApprovalsCount}</Badge> : undefined}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
							className="cv-exclude-from-customisation"
						>
							{formatMessage(messages.approvals)}
						</CustomItem>
					)}
				</Section>
			)}
			{canAdministerJIRA && (
				<Section hasSeparator>
					<CustomItem
						component={HtmlAnchor}
						actionSubjectId="navbarCustomiseHub"
						href={`/helpcenter-management/${helpCenterId}/edit-hub${editHubSuffix}`}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						className="cv-exclude-from-customisation"
					>
						{formatMessage(messages.customiseHub)}
					</CustomItem>
					<CustomItem
						component={HtmlAnchor}
						actionSubjectId="navbarHubSettings"
						href={`/helpcenter-management/hubs`}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						className="cv-exclude-from-customisation"
					>
						{formatMessage(messages.hubSettings)}
					</CustomItem>
				</Section>
			)}
		</>
	);
};

const mapStateToProps = (state: State): StateProps => {
	const userRequestsCounts = getUserRequestsCounts(state);
	return {
		myRequestCount: userRequestsCounts.myRequestCount,
		pendingApprovalCount: userRequestsCounts.pendingApprovalCount,
		approvalCount: userRequestsCounts.approvalCount,
		canAdministerJIRA: getCanAdministerJIRA(state),
	};
};

export const HubAvatarMenuOptions = connect(mapStateToProps, {
	fetchUser: fetchUserModelAction,
	fetchUserRequestsCounts: fetchUserRequestsCountsAction,
	fetchOrganisations: fetchOrganisationsModelAction,
})(HubAvatarMenuOptionsComponent);
